import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config.json";

const PrefillModal = ({ isOpen, onClose, onRefresh, selectedQuestions = [], reportingQuestionsMap = {}, financialYears = [], selectedFinancialYearId }) => {
  const [previousFinancialYear, setPreviousFinancialYear] = useState(null);
  const [currentFinancialYear, setCurrentFinancialYear] = useState(null);
  const [prefilling, setPrefilling] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [showConfirm, setShowConfirm] = useState(false);
  const [allowAllTypes, setAllowAllTypes] = useState(false);

  // Check if any selected question is quantitative
  const hasQuantitativeQuestions = selectedQuestions.some(questionId => {
    const question = reportingQuestionsMap?.[questionId];
    return question && question.questionType === 'quantitative_trends';
  });

  // Count total quantitative questions (for when no specific questions selected)
  const totalQuantitativeCount = reportingQuestionsMap
    ? Object.values(reportingQuestionsMap).filter(
        question => question && question.questionType === 'quantitative_trends'
      ).length
    : 0;

  /* ================= SET FINANCIAL YEARS FROM PROPS ================= */
  useEffect(() => {
    if (isOpen && financialYears.length >= 2) {
      const selectedIndex = financialYears.findIndex(y => y.id == selectedFinancialYearId);
      if (selectedIndex >= 0) {
        setCurrentFinancialYear(financialYears[selectedIndex]);
        if (selectedIndex < financialYears.length - 1) {
          setPreviousFinancialYear(financialYears[selectedIndex + 1]);
        } else {
          setPreviousFinancialYear({ financial_year_value: "Invalid financial year", id: null });
        }
      } else {
        setCurrentFinancialYear(financialYears[0]);
        setPreviousFinancialYear(financialYears[1]);
      }
      setError("");
      setSuccessMsg("");
      setShowConfirm(false);
    }
  }, [isOpen, financialYears, selectedFinancialYearId]);

  /* ================= FINAL PREFILL API (ONLY ON YES) ================= */
  const handleFinalPrefill = async () => {
    try {
      setPrefilling(true);
      setError("");
      setSuccessMsg("");

      const payload = {
        previousFinancialYearId: Number(previousFinancialYear.id),
        currentFinancialYearId: Number(currentFinancialYear.id),
        overwriteAllTypes: allowAllTypes,
      };

      // If specific questions are selected, send only those question IDs
      // If no questions are selected, don't send questionIds (all questions will be copied)
      if (selectedQuestions && selectedQuestions.length > 0) {
        payload.questionIds = selectedQuestions;
      }

      const response = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}prefillReportingFromPreviousYear`,
        {},
        payload,
        "POST"
      );

      if (response?.isSuccess) {
        const overwrittenCount = response?.data?.overwrittenCount || response?.data?.count || 0;
        const successMessage = overwrittenCount > 0
          ? `✅ ${overwrittenCount} question(s) have been overwritten successfully`
          : "✅ Data prefilled successfully";

        setSuccessMsg(successMessage);
        onRefresh?.();

        setTimeout(() => {
          setShowConfirm(false);
          onClose();
        }, 2000);
      } else {
        setError(response?.message || "Failed to prefill data");
      }
    } catch {
      setError("Server error occurred");
    } finally {
      setPrefilling(false);
    }
  };

  return (
    <>
      {/* ================= MAIN MODAL ================= */}
      <Modal show={isOpen} onHide={onClose} centered backdrop="static">
        <Modal.Header closeButton={!prefilling}>
          <Modal.Title style={{ color: "#3F88A5", fontWeight: 600 }}>
            Prefill from Previous Year
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {successMsg && <Alert variant="success">{successMsg}</Alert>}

          {currentFinancialYear && !previousFinancialYear?.id && (
            <Alert variant="warning">
              Previous year not available for {currentFinancialYear.financial_year_value}
            </Alert>
          )}

          {previousFinancialYear?.id && currentFinancialYear && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Copy From</Form.Label>
                <Form.Control
                  readOnly
                  value={previousFinancialYear.financial_year_value}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Copy To</Form.Label>
                <Form.Control
                  readOnly
                  value={currentFinancialYear.financial_year_value}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="allowAllTypes"
                  label="Allow all question types."
                  checked={allowAllTypes}
                  onChange={(e) => setAllowAllTypes(e.target.checked)}
                />
              </Form.Group>

              {allowAllTypes && (
                <Alert variant="info" style={{ fontSize: 13 }}>
                  ℹ️ All question types will be copied.
                </Alert>
              )}

              {selectedQuestions && selectedQuestions.length > 0 ? (
                <Alert variant="warning" style={{ fontSize: 13 }}>
                  <b>{selectedQuestions.length}</b> question(s) selected. Only
                  these questions will be prefilled.
                </Alert>
              ) : (
                <Alert variant="warning" style={{ fontSize: 13 }}>
                  No specific questions selected. <b>All questions</b> will be
                  prefilled.
                </Alert>
              )}

              {/* Show skipped count when no specific questions selected and allowAllTypes is false */}
              {!allowAllTypes && selectedQuestions.length === 0 && totalQuantitativeCount > 0 && (
                <Alert variant="info" style={{ fontSize: 13 }}>
                  ℹ️ <b>{totalQuantitativeCount}</b> quantitative question(s) will be skipped. Check "Allow all question types" to include them.
                </Alert>
              )}

              {hasQuantitativeQuestions && !allowAllTypes && (
                <Alert variant="danger" style={{ fontSize: 13 }}>
                  ⚠️ You have selected quantitative question(s). Please check "Allow all question types" to enable prefill.
                </Alert>
              )}
            </Form>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={prefilling}>
            Close
          </Button>

          <Button
            style={{ backgroundColor: "#3F88A5", border: "none" }}
            onClick={() => setShowConfirm(true)}
            disabled={prefilling || (hasQuantitativeQuestions && !allowAllTypes) || !previousFinancialYear?.id}
          >
            Confirm Prefill
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= CONFIRM MODAL ================= */}
      <Modal show={showConfirm} centered backdrop="static">
        <Modal.Header>
          <Modal.Title style={{ color: "#D9534F" }}>Are you sure?</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {prefilling ? (
            <div className="text-center">
              <Spinner animation="border" />
              <p className="mt-2">Copying data, please wait...</p>
            </div>
          ) : (
            <Alert variant="warning">
              ⚠️ Are you sure you want to proceed? 
              This action will overwrite existing data for the current financial year and cannot be undone.
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirm(false)}
            disabled={prefilling}
          >
            No
          </Button>

          <Button
            variant="danger"
            onClick={handleFinalPrefill}
            disabled={prefilling}
          >
            {prefilling ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Processing...
              </>
            ) : (
              "Yes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PrefillModal;
